import $ from 'jQuery';
import Vue from 'vue';
import buildFormData from '@/lib/formdata';
import { setUrlParameter } from '@/lib/url';
import { goto, parseError } from './helpers';

const messages = {
  saving: 'entries.autosaver.saving',
  saved: 'entries.autosaver.success',
  error: 'entries.autosaver.error'
};

const ajax = {
  get: url => Vue.prototype.$http.get(url),
  post: (url, data) => Vue.prototype.$http.post(url, data),
  postAsForm: (url, data) => Vue.prototype.$http.post(url, buildFormData(data))
};

export default {
  previewEntry({ getters, dispatch }) {
    dispatch('save').then(r => {
      if (r) {
        goto(getters.previewUrl);
      }
    });
  },

  saveAndPay({ getters, dispatch, rootGetters }) {
    const data = rootGetters['entryForm/data'];
    return dispatch('ajaxRequest', {
      method: 'postAsForm',
      uri: getters.payUrl,
      data: data,
      forceDirty: true
    });
  },

  submitForm({ dispatch, rootGetters, getters }) {
    const data = rootGetters['entryForm/data'];
    return dispatch('ajaxRequest', {
      method: 'postAsForm',
      uri: getters.submitUrl,
      data: data,
      forceDirty: true
    });
  },

  submit({ getters, dispatch, rootGetters }) {
    const data = rootGetters['entryForm/data'];
    return dispatch('ajaxRequest', {
      method: 'postAsForm',
      uri: getters.saveUrl,
      data: data
    }).then(r => dispatch('submitForm'));
  },

  save({ getters, dispatch, rootGetters }) {
    const data = rootGetters['entryForm/data'];
    return dispatch('ajaxRequest', {
      method: 'postAsForm',
      uri: getters.saveUrl,
      data: data
    }).then(r => {
      if (r) {
        if (getters.hasEntry && !getters.attachmentsLoaded) {
          dispatch('entryForm/loadAttachments', null, { root: true });
        }
        if (getters.hasEntry && !getters.contributorsLoaded) {
          dispatch('entryForm/loadContributors', null, { root: true });
        }
      }
      return r;
    });
  },

  saveAndNext({ dispatch, getters, rootGetters }) {
    const nextTabSlug = rootGetters['entryForm/nextTabSlug'];
    return dispatch('save').then(r => {
      if (r) {
        window.history.replaceState({ slug: null }, null, getters.editUrl);
        dispatch('setUriFromTabSlug', nextTabSlug);
        dispatch('entryForm/selectTabBySlug', nextTabSlug, { root: true });
        document.getElementById('entry-form').scrollIntoView();
      }
    });
  },

  saveAndClose({ getters, dispatch }) {
    return dispatch('save').then(r => {
      if (r) {
        goto(getters.routes.index());
      }
    });
  },

  close({ getters }) {
    return goto(getters.routes.index());
  },

  autosave({ dispatch, getters }) {
    if (getters.hasEntry) dispatch('save');
  },

  /**
   * Make an Ajax request
   *
   * * it sets Ajax busy flag
   * * it interprets response operation (redirect or switch)
   * * it updates the form with response data
   * * it eventually updates the errorBag
   *
   * @param method - a method name, an existing key from 'ajax = { ... }'
   * @param uri - request URI
   * @param data - data to pass with the request
   * @param forceDirty - make the request even if the form isn't dirty
   */
  ajaxRequest(
    { state, commit, getters, dispatch },
    { method, uri, data, forceDirty }
  ) {
    if (
      !getters.isLoaded ||
      !getters.isSaveable ||
      state.ajaxInProgress ||
      getters.configurationMode ||
      (!getters.isFormDirty && forceDirty !== true)
    ) {
      return Promise.resolve({});
    }
    commit('setMessageType', '');
    commit('setMessage', messages.saving);
    commit('setAjaxInProgress', true);
    return ajax[method || 'get'](uri, data || null).then(
      response => {
        dispatch('updateForm', response.data);
        commit('setAjaxInProgress', false);
        commit('setMessageType', 'success');
        commit('setMessage', messages.saved);
        new Promise(resolve => setTimeout(resolve, 5000)).then(() =>
          commit('setMessage')
        );
        dispatch('entryForm/setClean', null, { root: true });
        
        // Shouldn't we clear the errors since the request didn't return any errors?
        commit('setErrorBag', []);

        if (response.data.redirect) {
          goto(getters.routes[response.data.redirect](response.data));
          return false;
        }
        return response.data;
      },
      error => {
        const errorBag = {};
        const extraData = {};
        const extraDataKey = /error-response-extra-data-(.*)/i;
        let keyData;

        // Handle 403 Forbidden error
        // Handle 419 Authentication Timeout error
        if (
          error.response &&
          error.response.data &&
          [403, 419].includes(error.response.status)
        ) {
          commit(
            'entryForm/storeStatus',
            {
              status: 'error',
              message: error.response.data.message || messages.error
            },
            { root: true }
          );
          commit('setAjaxInProgress', false);
          commit('setMessageType', 'error');
          commit('setMessage', messages.error);
          return null;
        }

        // Handle validation errors
        if (error.response && error.response.data) {
          Object.keys(error.response.data).forEach(key => {
            if ((keyData = extraDataKey.exec(key))) {
              extraData[keyData[1]] = error.response.data[key][0];
            } else {
              errorBag[key] = error.response.data[key];
            }
          });
          let apiErrors = Object.entries(
            (errorBag.trace ? [[errorBag.message]] : errorBag) || {}
          ).map(entry => parseError(entry[0], entry[1]));
          commit('setErrorBag', apiErrors);
          commit('entryForm/storeApiErrors', apiErrors, { root: true });
          dispatch('entryForm/focusErrorsTab', null, { root: true });
          dispatch('updateForm', extraData);
        } else if (error.isAxiosError || error.trace) {
          commit('setErrorBag', [[error.message]]);
        } else {
          commit('setErrorBag', {});
        }

        commit('setAjaxInProgress', false);
        commit('setMessageType', 'error');
        commit('setMessage', messages.error);

        $(window).scrollTop(0);
        return null;
      }
    );
  },

  /**
   * Update the form
   *
   * @param entry - entry data if received
   * @param updatedAt - request timestamp
   * @param contributors - saved contributors ids if received
   *
   * @TODO: add attachments managment here
   */
  updateForm(
    { commit, rootGetters, getters },
    { entry, updatedAt, contributors }
  ) {
    if (entry) {
      commit('entryForm/storeEntry', entry, { root: true });
      const tabSlug = rootGetters['entryForm/selectedTab'].slug || '';
      window.history.replaceState(
        { slug: tabSlug },
        null,
        setUrlParameter(getters.editUrl, 'tabSlug', tabSlug)
      );
    }
    if (updatedAt) commit('entryForm/setTimestamp', updatedAt, { root: true });
    if (contributors)
      commit('entryForm/updateContributors', contributors, { root: true });
  },

  setUriFromTab({ rootGetters, dispatch }, tabId) {
    const tab = rootGetters['entryForm/tabById'](tabId);
    dispatch('setUriFromTabSlug', tab ? tab.slug : null);
  },

  setUriFromTabSlug(context, tabSlug) {
    window.history.pushState(
      { slug: tabSlug },
      null,
      setUrlParameter(window.location.href, 'tabSlug', tabSlug)
    );
  },

  /**
   * Reset store state
   */
  resetStoreState({ commit }) {
    commit('resetState');
  }
};
